@if (universalSearchService.isOpen()) {
  <div class="fixed inset-0 z-50 flex items-start justify-center px-4 pt-16 sm:pt-24" role="dialog" aria-modal="true" aria-labelledby="universalSearchTitle">
    <div class="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity dark:bg-black/70" (click)="universalSearchService.close()" aria-hidden="true"></div>

    <div class="relative w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5 dark:bg-dark-bg-content dark:ring-white/10">
      <h2 id="universalSearchTitle" class="sr-only">Search this workspace</h2>

      <div class="flex items-center gap-x-3 border-b border-gray-200 px-4 dark:border-dark-bg-icon">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5 flex-shrink-0 text-text-placeholder dark:text-dark-text-subtitle" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
        </svg>

        <input
          #searchInput
          id="universalSearchInput"
          type="text"
          autocomplete="off"
          aria-label="Search this workspace"
          placeholder="Search inspections, folders, clients, sites…"
          class="h-14 w-full border-0 bg-transparent p-0 text-base text-text-title placeholder:text-text-placeholder focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-dark-text-placeholder"
          [value]="universalSearchService.query()"
          (input)="onInput($event)"
        />

        <span class="hidden flex-shrink-0 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-subtitle dark:border-dark-bg-icon dark:text-dark-text-subtitle sm:inline-block">esc</span>
      </div>

      @if (universalSearchService.activeInspectionTitle()) {
        <div class="flex items-center gap-x-2 border-b border-gray-100 px-4 py-2 dark:border-dark-bg-icon">
          <span
            class="inline-flex items-center rounded-full border border-brand-primary/25 bg-brand-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-primary dark:border-dark-brand-primary/40 dark:bg-dark-brand-primary/10 dark:text-dark-brand-primary"
            >Open inspection</span
          >
          <span class="truncate text-xs text-text-subtitle dark:text-dark-text-subtitle">{{ universalSearchService.activeInspectionTitle() }}</span>
        </div>
      }

      <div class="max-h-[60vh] overflow-y-auto">
        @if (!hasQuery()) {
          @if (universalSearchService.recents().length > 0) {
            <div class="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-text-subtitle dark:text-dark-text-subtitle">Recent</div>
            @for (recent of universalSearchService.recents(); track recent.kind + recent.id) {
              <button type="button" class="flex w-full items-center gap-x-3 px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-dark-bg-view" (click)="universalSearchService.select(recent)">
                <span
                  class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-brand-primary/25 bg-brand-primary/10 text-brand-primary dark:border-dark-brand-primary/40 dark:bg-dark-brand-primary/10 dark:text-dark-brand-primary"
                >
                  <universal-search-icon [kind]="recent.kind" />
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-sm font-medium text-text-title dark:text-white">{{ recent.title }}</span>
                  @if (recent.subtitle) {
                    <span class="block truncate text-xs text-text-subtitle dark:text-dark-text-subtitle">{{ recent.subtitle }}</span>
                  }
                </span>
                <span class="flex-shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-text-subtitle dark:bg-dark-bg-icon dark:text-dark-text-subtitle">{{ kindLabel(recent.kind) }}</span>
              </button>
            }
          }

          @if (universalSearchService.quickLinks().length > 0) {
            <div class="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-text-subtitle dark:text-dark-text-subtitle">Jump to</div>
            <div class="flex flex-wrap gap-2 px-4 pb-4 pt-1">
              @for (link of universalSearchService.quickLinks(); track link.label) {
                <button
                  type="button"
                  class="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-text-subtitle hover:border-brand-primary hover:text-brand-primary dark:border-dark-bg-icon dark:text-dark-text-subtitle dark:hover:border-dark-brand-primary dark:hover:text-dark-brand-primary"
                  (click)="universalSearchService.goTo(link.commands)"
                >
                  {{ link.label }}
                </button>
              }
            </div>
          }

          <p class="px-4 pb-4 text-xs text-text-subtitle dark:text-dark-text-subtitle">{{ minimumQueryHint() }}</p>
        } @else if (indexedGroups().length === 0) {
          @if (universalSearchService.status() === 'error') {
            <p class="px-4 py-8 text-center text-sm text-brand-destructive dark:text-dark-brand-destructive">Search is unavailable right now. Please try again.</p>
          } @else if (universalSearchService.status() === 'loading') {
            <p class="px-4 py-8 text-center text-sm text-text-subtitle dark:text-dark-text-subtitle">Searching…</p>
          } @else {
            <p class="px-4 py-8 text-center text-sm text-text-subtitle dark:text-dark-text-subtitle">No matches for “{{ universalSearchService.query() }}”.</p>
          }
        } @else {
          @for (group of indexedGroups(); track group.kind) {
            <div class="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-text-subtitle dark:text-dark-text-subtitle">{{ group.label }}</div>
            @for (entry of group.results; track entry.result.kind + entry.result.id) {
              <button
                type="button"
                [id]="'universalSearchResult' + entry.index"
                class="flex w-full items-center gap-x-3 px-4 py-2.5 text-left"
                [class]="activeIndex() === entry.index ? 'bg-brand-primary/10 dark:bg-dark-brand-primary/20' : 'hover:bg-gray-50 dark:hover:bg-dark-bg-view'"
                (mouseenter)="activeIndex.set(entry.index)"
                (click)="universalSearchService.select(entry.result)"
              >
                <span
                  class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-brand-primary/25 bg-brand-primary/10 text-brand-primary dark:border-dark-brand-primary/40 dark:bg-dark-brand-primary/10 dark:text-dark-brand-primary"
                >
                  <universal-search-icon [kind]="entry.result.kind" />
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-sm font-medium text-text-title dark:text-white">{{ entry.result.title }}</span>
                  @if (entry.result.subtitle) {
                    <span class="block truncate text-xs text-text-subtitle dark:text-dark-text-subtitle">{{ entry.result.subtitle }}</span>
                  }
                </span>
                <span class="flex-shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-text-subtitle dark:bg-dark-bg-icon dark:text-dark-text-subtitle">{{ kindLabel(entry.result.kind) }}</span>
              </button>
            }
          }

          @if (universalSearchService.status() === 'error') {
            <p class="border-t border-gray-100 px-4 py-3 text-xs text-brand-destructive dark:border-dark-bg-icon dark:text-dark-brand-destructive">Workspace search is unavailable, showing open inspection results only.</p>
          } @else if (universalSearchService.truncated()) {
            <p class="px-4 py-3 text-xs text-text-subtitle dark:text-dark-text-subtitle">Showing the closest matches. Refine your search to narrow the list.</p>
          }
        }
      </div>

      <div class="hidden items-center gap-x-4 border-t border-gray-200 px-4 py-2 text-[11px] text-text-subtitle dark:border-dark-bg-icon dark:text-dark-text-subtitle sm:flex">
        <span><span class="font-semibold">↑↓</span> to navigate</span>
        <span><span class="font-semibold">↵</span> to open</span>
        <span><span class="font-semibold">esc</span> to close</span>
      </div>
    </div>
  </div>
}
